import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Loader2, Shield, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config/api";

// Hardcoded OTP recipients — mirrors backend SUPER_ADMIN_OTP_RECIPIENTS
const OTP_RECIPIENTS = [
  { key: "ryan", label: "Ryan", email: "ryan@bookingflows.com" },
];

const PlatformAdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  
  // step 1 = credentials, 2 = choose recipient, 3 = enter OTP
  const [step, setStep] = useState(1);
  const [twoFACode, setTwoFACode] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");

  // Step 1: verify credentials
  const handleSendCredentials = async (e) => {
    e.preventDefault();
    if (!username || !password) { toast.error("Please enter both username and password"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/v2/super-admin/auth/login`, { username, password });
      if (response.data.success) {
        if (response.data.requires_2fa) {
          // Single super-admin recipient — send the code straight to Ryan.
          await handleChooseRecipient("ryan");
        } else {
          // No 2FA — direct login
          localStorage.setItem("jwtToken", response.data.token);
          localStorage.setItem("userType", "super_admin");
          localStorage.setItem("userData", JSON.stringify({ username: response.data.username, name: response.data.name || "Super Admin" }));
          toast.success("Welcome, Super Admin!");
          navigate("/super-admin");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: choose who receives the OTP
  const handleChooseRecipient = async (recipientKey) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/v2/super-admin/auth/send-otp`, {
        username,
        recipient_key: recipientKey,
      });
      setMaskedEmail(response.data.masked_email);
      setTwoFACode("");
      setStep(3);
      toast.success("Verification code sent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: verify OTP
  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!twoFACode || twoFACode.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/v2/super-admin/auth/verify-otp`, {
        username,
        code: twoFACode,
      });
      if (response.data.success) {
        localStorage.setItem("jwtToken", response.data.token);
        localStorage.setItem("userType", "super_admin");
        localStorage.setItem("userData", JSON.stringify({ username: response.data.username, name: response.data.name }));
        toast.success("Welcome, Super Admin!");
        navigate("/super-admin");
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Invalid code";
      toast.error(msg);
      if (msg.includes("expired") || msg.includes("not found")) {
        setStep(2); setTwoFACode("");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: OTP entry ─────────────────────────────────────────────────────
  if (step === 3) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="w-full max-w-sm text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto border-2 border-blue-400/50">
              <Lock className="w-10 h-10 text-blue-400" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Two-Factor Authentication</h1>
          <p className="text-blue-200 text-sm mb-8">
            Code sent to<br/><span className="text-white font-medium">{maskedEmail}</span>
          </p>
          <form onSubmit={handleVerify2FA} className="space-y-6">
            <div className="flex justify-center gap-2">
              {[0,1,2,3,4,5].map((i) => (
                <input
                  key={i} id={`pin-${i}`}
                  type="text" inputMode="numeric" maxLength={1}
                  value={twoFACode[i] || ''}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '');
                    if (val.length <= 1) {
                      const arr = twoFACode.split('');
                      arr[i] = val;
                      setTwoFACode(arr.join(''));
                      if (val && i < 5) document.getElementById(`pin-${i+1}`)?.focus();
                    }
                  }}
                  onKeyDown={(e) => { if (e.key === 'Backspace' && !twoFACode[i] && i > 0) document.getElementById(`pin-${i-1}`)?.focus(); }}
                  onPaste={(e) => { e.preventDefault(); setTwoFACode(e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6)); }}
                  disabled={loading}
                  autoFocus={i === 0}
                  className="w-12 h-14 text-center text-2xl font-bold bg-white/10 border-2 border-blue-400/50 rounded-lg text-white focus:border-blue-400 focus:bg-white/20 focus:outline-none transition-all"
                />
              ))}
            </div>
            <Button type="submit" className="w-full h-12 text-base font-semibold bg-blue-500 hover:bg-blue-600 text-white" disabled={loading || twoFACode.length !== 6}>
              {loading ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Verifying...</span> : "Verify & Sign In"}
            </Button>
          </form>
          <div className="mt-6 flex items-center justify-between text-sm">
            <button onClick={() => { setStep(2); setTwoFACode(''); }} className="text-blue-300 hover:text-white transition-colors">← Back</button>
            <button onClick={() => { setStep(2); setTwoFACode(''); }} disabled={loading} className="text-blue-300 hover:text-white font-medium transition-colors">Change recipient</button>
          </div>
          <p className="text-xs text-blue-300/70 mt-4">Code expires in 10 minutes</p>
        </div>
      </div>
    );
  }

  // ── Step 2: Choose OTP recipient ──────────────────────────────────────────
  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="w-full max-w-sm text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto border-2 border-blue-400/50">
              <Mail className="w-10 h-10 text-blue-400" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Send Verification Code</h1>
          <p className="text-blue-200 text-sm mb-8">Choose who receives the one-time code</p>
          <div className="space-y-3">
            {OTP_RECIPIENTS.map((r) => (
              <button
                key={r.key}
                data-testid={`otp-recipient-${r.key}`}
                onClick={() => handleChooseRecipient(r.key)}
                disabled={loading}
                className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-white/10 border border-blue-400/30 hover:bg-white/20 hover:border-blue-400 transition-all disabled:opacity-50"
              >
                <span className="text-white font-semibold text-lg">{r.label}</span>
                <span className="text-blue-300 text-sm">{r.email}</span>
              </button>
            ))}
          </div>
          <button onClick={() => setStep(1)} className="mt-6 text-blue-300 hover:text-white text-sm transition-colors">← Back</button>
        </div>
      </div>
    );
  }

  // ── Step 1: Credentials ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg" style={{background: '#007AFF'}}>
              <Shield className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            BookingFlows
          </CardTitle>
          <p className="text-gray-600 mt-2">Platform Administrator Access</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSendCredentials} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-semibold text-gray-700">Username</Label>
              <Input id="username" type="text" placeholder="Enter admin username" value={username} onChange={(e) => setUsername(e.target.value)} disabled={loading} className="h-12 text-base border-2 border-gray-200 focus:border-blue-500" autoFocus />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-gray-700">Password</Label>
              <Input id="password" type="password" placeholder="Enter admin password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading} className="h-12 text-base border-2 border-gray-200 focus:border-blue-500" />
            </div>
            <Button type="submit" className="w-full h-12 text-base font-semibold mt-6" style={{background: '#007AFF'}} disabled={loading}>
              {loading ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Verifying...</span> : <><Shield className="w-4 h-4 mr-2"/>Continue</>}
            </Button>
          </form>
        </CardContent>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} BookingFlows. All rights reserved.</p>
        </div>
      </Card>
    </div>
  );
};

export default PlatformAdminLogin;
